import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {  FiHeadphones } from "react-icons/fi";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import ProductCard from "../../components/RudrakshaSection/ProductCard";
import CategorySearch from "../../components/CategorySearch/CategorySearch";
import Loader from "../../components/Loader/Loader";
import menuData from "../../components/Header/MenuData";

import { getProducts } from "../../api/getProducts";
import { getCategories } from "../../api/getCategories";

import poojaBanner from "../../assets/images/pooja-essentials-banner.webp";

import "./CategoryProducts.css";

export default function CategoryProducts() {
  const { mainCategory, subCategory } = useParams();

  const [products, setProducts] = useState([]);
  const [currentSubcategory, setCurrentSubcategory] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const [search, setSearch] = useState("");

  const limit = 12;

  // ==========================================
  // NORMALIZE SLUG
  // ==========================================

  const normalizeSlug = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

  // ==========================================
  // FIND CATEGORY FROM MENU
  // ==========================================

  const shop = menuData.find(
    (item) => item.title === "The Shoppe"
  );

  const menuCategory = shop?.categories?.find(
    (category) =>
      normalizeSlug(category?.slug) ===
      normalizeSlug(mainCategory)
  );

  // ==========================================
  // FETCH CATEGORY + SUBCATEGORY + PRODUCTS
  // ==========================================

  useEffect(() => {
    // Aborted when the route / page / search changes again, so an older,
    // slower response can never overwrite a newer one.
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        setProducts([]);
        setPagination(null);

        // ======================================
        // MENU CATEGORY CHECK
        // ======================================

        if (!menuCategory) {
          setError("Category Not Found");
          return;
        }

        console.log(
          "MENU CATEGORY:",
          menuCategory
        );

        // ======================================
        // GET ALL CATEGORIES FROM API
        // ======================================

        const categoryResult = await getCategories();

        console.log(
          "CATEGORY API RESPONSE:",
          categoryResult
        );

        // ======================================
        // CHECK API RESPONSE
        // ======================================

        if (
          !categoryResult?.success ||
          !Array.isArray(categoryResult?.data)
        ) {
          throw new Error(
            categoryResult?.message ||
              "Unable to load categories."
          );
        }

        const categories =
          categoryResult.data;

        // ======================================
        // FIND CATEGORY BY SLUG / NAME / TITLE
        // ======================================

        const currentCategory =
          categories.find((category) => {
            const apiSlug = normalizeSlug(
              category?.slug
            );

            const apiName = normalizeSlug(
              category?.name
            );

            const apiTitle = normalizeSlug(
              category?.title
            );

            const urlSlug =
              normalizeSlug(mainCategory);

            return (
              apiSlug === urlSlug ||
              apiName === urlSlug ||
              apiTitle === urlSlug
            );
          });

        console.log(
          "CURRENT API CATEGORY:",
          currentCategory
        );

        console.log(
          "URL CATEGORY:",
          mainCategory
        );

        // ======================================
        // CATEGORY NOT FOUND
        // ======================================

        if (!currentCategory) {
          setError(
            "Category not found in API."
          );
          return;
        }

        // ======================================
        // CATEGORY ID
        // ======================================

        const categoryId =
          currentCategory.id;

        console.log(
          "CATEGORY ID:",
          categoryId
        );

        // ======================================
        // GET SUBCATEGORIES FROM CATEGORY
        // ======================================

        const subcategories =
          Array.isArray(
            currentCategory.subcategories
          )
            ? currentCategory.subcategories
            : [];

        console.log(
          "SUBCATEGORIES:",
          subcategories
        );

        // ======================================
        // FIND CURRENT SUBCATEGORY
        // ======================================

        const matchedSubcategory =
          subcategories.find(
            (subcategory) => {
              const apiSlug =
                normalizeSlug(
                  subcategory?.slug
                );

              const apiName =
                normalizeSlug(
                  subcategory?.name
                );

              const apiTitle =
                normalizeSlug(
                  subcategory?.title
                );

              const urlSlug =
                normalizeSlug(subCategory);

              return (
                apiSlug === urlSlug ||
                apiName === urlSlug ||
                apiTitle === urlSlug
              );
            }
          );

        console.log(
          "MATCHED SUBCATEGORY:",
          matchedSubcategory
        );

        // ======================================
        // SUBCATEGORY NOT FOUND
        // ======================================

        if (!matchedSubcategory) {
          setError(
            "Subcategory Not Found"
          );
          return;
        }

        // ======================================
        // SET CURRENT SUBCATEGORY
        // ======================================

        setCurrentSubcategory(
          matchedSubcategory
        );

        // ======================================
        // SUBCATEGORY ID
        // ======================================

        const subcategoryId =
          matchedSubcategory.id;

        console.log(
          "SUBCATEGORY ID:",
          subcategoryId
        );

        if (!subcategoryId) {
          setError(
            "Subcategory ID not found."
          );
          return;
        }

        // ======================================
        // GET PRODUCTS
        // ======================================

        const productResponse =
          await getProducts({
            categoryId: categoryId,
            subcategoryId: subcategoryId,
            search,
            page: page,
            limit: limit,
            signal: controller.signal,
          });

        console.log(
          "PRODUCT API RESPONSE:",
          productResponse
        );

        // ======================================
        // PRODUCT DATA
        // ======================================

        const productData =
          productResponse?.data;

        const productList =
          productData?.products || [];

        console.log(
          "PRODUCT LIST:",
          productList
        );

        setProducts(productList);

        // ======================================
        // PAGINATION
        // ======================================

        setPagination(
          productData?.pagination || null
        );
      } catch (error) {
        if (controller.signal.aborted) return;

        console.error(
          "Category Products Error:",
          error
        );

        setError(
          error?.message ||
            "Something went wrong while loading products."
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => controller.abort();
  }, [
    mainCategory,
    subCategory,
    page,
    search,
    menuCategory?.slug,
  ]);

  useEffect(() => {
    setPage(1);
    setSearch("");
    setCurrentSubcategory(null);
  }, [mainCategory, subCategory]);

  const handleSearch = (term) => {
    setSearch(term);
    setPage(1);
  };

  // ==========================================
  // CATEGORY NOT FOUND
  // ==========================================

  if (!menuCategory) {
    return (
      <>
        <Header />

        <section className="category-products">
          <div className="no-products">
            <h2>
              Category Not Found
            </h2>
          </div>
        </section>

        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

    {/* {mainCategory === "pooja" ? (
  <section style={{ width: "100%" }}>
    <img
      src={poojaBanner}
      alt={menuCategory.title || "Pooja Essentials"}
      style={{
        width: "100%",
        display: "block",
      }}
    />
  </section>
) : (
  <section className="category-banner">
    <h1 className="astroBannerHeading">
      {currentSubcategory?.name ||
        currentSubcategory?.title ||
        menuCategory.title ||
        "Products"}
    </h1>
  </section>
)} */}


      <div className="breadcrumb">
        <Link to="/">
          Home
        </Link>

        <span> / </span>

        <Link
          to={`/shop/${mainCategory}`}
        >
          {menuCategory.title}
        </Link>

        <span> / </span>

        <span>
          {currentSubcategory?.name ||
            currentSubcategory?.title ||
            subCategory}
        </span>
      </div>
      <section className="category-products">

        {/* ====================================
            SEARCH WITHIN SUBCATEGORY
        ===================================== */}

        <div className="category-products-toolbar">
          <CategorySearch
            key={`${mainCategory}/${subCategory}`}
            onSearch={handleSearch}
            placeholder={`Search in ${
              currentSubcategory?.name || menuCategory.title
            }...`}
          />
        </div>

        {/* ====================================
            LOADING
        ===================================== */}

        {loading && (
          <div className="products-loading">
            <Loader label="Loading products..." />
          </div>
        )}

        {/* ====================================
            ERROR
        ===================================== */}

        {!loading && error && (
          <div className="products-error">
            <h2>
              {error}
            </h2>
          </div>
        )}

        {/* ====================================
            PRODUCTS
        ===================================== */}

        {!loading &&
          !error &&
          products.length > 0 && (
            <>
              <div className="products-grid">
                {products.map(
                  (product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                    />
                  )
                )}
              </div>

              {/* ================================
                  PAGINATION
              ================================= */}

              {pagination &&
                pagination.pages > 1 && (
                  <div className="pagination">

                    {Array.from(
                      {
                        length:
                          pagination.pages,
                      },
                      (_, index) =>
                        index + 1
                    ).map(
                      (pageNumber) => (
                        <button
                          key={pageNumber}
                          type="button"
                          className={
                            page ===
                            pageNumber
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            setPage(
                              pageNumber
                            )
                          }
                        >
                          {pageNumber}
                        </button>
                      )
                    )}

                  </div>
                )}
            </>
          )}

        {/* ====================================
            NO PRODUCTS
        ===================================== */}

        {!loading &&
          !error &&
          products.length === 0 && (
            <div className="no-products">
              <h2>
                {search
                  ? `No products match “${search}”`
                  : "No Products Found"}
              </h2>
            </div>
          )}

      </section>
  <section className="support-section">
        <div className="support-content">
          <h2>Need Support? Get Consultation Now!</h2>
          <p>Need to chat with us? We are happy to assist you.</p>

        <Link to="/contact" className="support-btn">
  <FiHeadphones />
  Get Support
</Link>
        </div>
      </section>
      <Footer />
    </>
  );
}